"use client"

import * as React from "react"
import { Button } from "./button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select"
import { Label } from "./label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card"
import { Pill } from "lucide-react"

export interface MedicationOption {
  id: string
  name: string
  description?: string
  category: 'weight_loss' | 'mens_health' | 'general'
  available_dosages: string[]
  image_url?: string
}

export interface MedicationPreferencesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  medications: MedicationOption[]
  onSubmit?: (preferences: { medicationId: string; dosage: string; isEdit?: boolean; preferenceId?: string }) => void
  onAddAnother?: (preferences: { medicationId: string; dosage: string }) => void
  loading?: boolean
  editMode?: boolean
  isOnboarding?: boolean
  currentMedication?: {
    id?: string
    medicationId?: string
    medicationName?: string
    dosage?: string
  }
}

export function MedicationPreferencesDialog({
  open,
  onOpenChange,
  medications = [],
  onSubmit,
  onAddAnother,
  loading = false,
  editMode = false,
  isOnboarding = false,
  currentMedication
}: MedicationPreferencesDialogProps) {
  const [selectedMedicationId, setSelectedMedicationId] = React.useState<string>("")
  const [selectedDosage, setSelectedDosage] = React.useState<string>("")

  const selectedMedication = medications.find(med => med.id === selectedMedicationId)
  const availableDosages = selectedMedication?.available_dosages || []

  const handleSubmit = () => {
    // Allow Doctor Recommendation without dosage selection
    const isDoctorRecommendation = selectedMedicationId === 'doctor-recommendation'
    const isValid = selectedMedicationId && (isDoctorRecommendation || selectedDosage)
    
    if (isValid && onSubmit) {
      onSubmit({
        medicationId: selectedMedicationId,
        dosage: selectedDosage || (isDoctorRecommendation ? 'Provider Assessment' : ''),
        isEdit: editMode,
        preferenceId: currentMedication?.id
      })
    }
  }

  const handleAddAnother = () => {
    // Allow Doctor Recommendation without dosage selection
    const isDoctorRecommendation = selectedMedicationId === 'doctor-recommendation'
    const isValid = selectedMedicationId && (isDoctorRecommendation || selectedDosage)
    
    if (isValid && onAddAnother) {
      onAddAnother({
        medicationId: selectedMedicationId,
        dosage: selectedDosage || (isDoctorRecommendation ? 'Provider Assessment' : '')
      })
      // Reset form for next entry but keep dialog open
      setSelectedMedicationId("")
      setSelectedDosage("")
    }
  }

  const handleMedicationChange = (medicationId: string) => {
    setSelectedMedicationId(medicationId)
    
    // Reset dosage when medication changes, or auto-select for Doctor Recommendation
    if (medicationId === 'doctor-recommendation') {
      setSelectedDosage('Provider Assessment')
    } else {
      setSelectedDosage("")
    }
  }

  React.useEffect(() => {
    if (!open) {
      setSelectedMedicationId("")
      setSelectedDosage("")
    }
  }, [open])

  // Pre-populate form when editing existing medication
  React.useEffect(() => {
    if (editMode && currentMedication && open) {
      console.log('🔍 Dialog: Attempting to find medication for edit mode')
      console.log('🔍 Dialog: currentMedication:', currentMedication)
      console.log('🔍 Dialog: available medications:', medications)
      console.log('🔍 Dialog: looking for medicationId:', currentMedication.medicationId)
      console.log('🔍 Dialog: looking for medicationName:', currentMedication.medicationName)
      console.log('🔍 Dialog: available medication names:', medications.map(m => m.name))
      
      // Find medication by name if medicationId not available
      const medication = currentMedication.medicationId 
        ? medications.find(med => med.id === currentMedication.medicationId)
        : medications.find(med => {
            // Try exact match first
            if (med.name === currentMedication.medicationName) return true
            // Try partial match (e.g., "Tirzepatide" matches "Tirzepatide (Mounjaro)")
            if (med.name.toLowerCase().includes(currentMedication.medicationName?.toLowerCase() || '')) return true
            // Try reverse partial match (e.g., "Tirzepatide (Mounjaro)" contains "Tirzepatide")
            if (currentMedication.medicationName?.toLowerCase().includes(med.name.toLowerCase())) return true
            return false
          })
      
      console.log('🔍 Dialog: Found medication match:', medication)
      
      if (medication) {
        console.log('✅ Dialog: Setting medication ID:', medication.id)
        setSelectedMedicationId(medication.id)
        setSelectedDosage(currentMedication.dosage || "")
      } else {
        console.log('❌ Dialog: No medication match found')
      }
    }
  }, [editMode, currentMedication, medications, open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-4 gap-2">
        <DialogHeader className="space-y-1">
          <DialogTitle>
            {editMode ? 'Edit Medication Preference' : 'Select Medications'}
          </DialogTitle>
          <DialogDescription>
            {editMode
              ? 'Update your medication preference. Changes will reset the status to pending for provider review.'
              : 'Choose your preferred medication and dosage. This will be reviewed by your provider before your visit.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="medication">Medication</Label>
              {editMode ? (
                <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm items-center">
                  <span className="truncate">
                    {selectedMedication?.name || 'Unknown Medication'}
                  </span>
                </div>
              ) : (
                <Select 
                  value={selectedMedicationId} 
                  onValueChange={handleMedicationChange}
                >
                  <SelectTrigger id="medication" className="w-full">
                    <SelectValue placeholder="Select a medication">
                      {selectedMedication && (
                        <span className="truncate">{selectedMedication.name}</span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-48px)]">
                    {medications.map((medication) => {
                      console.log(`🖼️ UI: Rendering medication "${medication.name}" with image_url:`, medication.image_url)
                      return (
                        <SelectItem key={medication.id} value={medication.id}>
                        <div className="flex items-center gap-3 max-w-full">
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                            {medication.image_url ? (
                              <img 
                                src={medication.image_url}
                                alt={medication.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  console.log(`❌ UI: Image failed to load for "${medication.name}":`, medication.image_url)
                                  e.currentTarget.style.display = 'none'
                                  const parent = e.currentTarget.parentElement
                                  if (parent) {
                                    parent.innerHTML = `<div class="w-6 h-6 rounded bg-primary/20 flex items-center justify-center"><span class="text-xs font-medium text-primary">${medication.name.charAt(0)}</span></div>`
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center">
                                <span className="text-xs font-medium text-primary">
                                  {medication.name.charAt(0)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="font-medium truncate">{medication.name}</span>
                            {medication.description && (
                              <span className="text-sm text-muted-foreground break-words">
                                {medication.description}
                              </span>
                            )}
                          </div>
                        </div>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="dosage">Dosage</Label>
              <Select 
                value={selectedDosage} 
                onValueChange={setSelectedDosage}
                disabled={!selectedMedicationId || selectedMedicationId === 'doctor-recommendation'}
              >
                <SelectTrigger id="dosage" className="w-full">
                  <SelectValue placeholder={
                    selectedMedicationId === 'doctor-recommendation' 
                      ? "Provider will determine dosage" 
                      : selectedMedicationId 
                        ? "Select dosage" 
                        : "Select medication first"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {availableDosages.map((dosage) => (
                    <SelectItem key={dosage} value={dosage}>
                      {dosage}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          {!editMode && !isOnboarding && onAddAnother && (
            <Button 
              variant="outline"
              onClick={handleAddAnother}
              disabled={!selectedMedicationId || (!selectedDosage && selectedMedicationId !== 'doctor-recommendation') || loading}
            >
              {loading ? "Adding..." : "Add Another +"}
            </Button>
          )}
          <Button 
            onClick={handleSubmit}
            disabled={!selectedMedicationId || (!selectedDosage && selectedMedicationId !== 'doctor-recommendation') || loading}
          >
            {loading ? "Saving..." : editMode ? "Update Preference" : "Save Preference"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}